import { Checkbox } from '../../../components/ui/checkbox';
import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';

const graphOptions = [
  {
    key: 'user',
    title: 'You',
    color: 'var(--q3-orange-normal)',
    checked: true,
  },
  {
    key: 'topper',
    title: 'Topper',
    color: 'var(--q3-green-normal)',
    checked: true,
  },
  {
    key: 'top_10_percent',
    title: 'Top 10%ile',
    color: 'var(--q3-blue-normal)',
    checked: true,
  },
  {
    key: 'top_25_percent',
    title: 'Top 25%ile',
    color: 'var(--q3-yellow-normal)',
    checked: true,
  },
];

const ATTEMPT_TYPES = ['Perfect', 'Wasted', 'Overtime', 'Confused'];

export default function ComparativeAttemptAnalysisChart({
  selectedSubject,
  comparativeAttemptAnalysis,
  userAttemptAnalysis,
}) {
  const [selectedGraphOptions, setSelectedGraphOptions] = useState(graphOptions);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    if (!selectedSubject) return;

    const subjectId = selectedSubject?.id?.toString();

    const userData = userAttemptAnalysis?.find((d) => d.subject_id?.toString() === subjectId);
    const comparativeData = comparativeAttemptAnalysis?.find((d) => d.subject_id?.toString() === subjectId);

    if (!userData || !comparativeData) return;

    const data = ATTEMPT_TYPES.map((type) => {
      const entry = { attemptType: type };

      const userVal = userData?.[`${type.toLowerCase()}_attempts`] ?? 0;
      const match = comparativeData?.values?.find((v) => v.title === type);

      const topperVal = match?.values?.topper ?? 0;
      const top10Val = match?.values?.top_10_percent ?? 0;
      const top25Val = match?.values?.top_25_percent ?? 0;

      // Use 0.15 for zero values to show thin bars
      entry['You'] = userVal || 0.15;
      entry['Topper'] = topperVal === '0' ? 0.15 : topperVal;
      entry['Top 10%ile'] = top10Val === '0' ? 0.15 : top10Val;
      entry['Top 25%ile'] = top25Val === '0' ? 0.15 : top25Val;

      return entry;
    });

    setChartData(data);
  }, [selectedSubject, comparativeAttemptAnalysis, userAttemptAnalysis]);

  const handleOnChange = (index) => {
    setSelectedGraphOptions((prev) => prev.map((opt, i) => (i === index ? { ...opt, checked: !opt.checked } : opt)));
  };

  const visibleGraphOptions = selectedGraphOptions.filter((opt) => opt.checked);

  const CustomBarLabel = ({ x, y, width, value }) => {
    const actual = Math.round(value);
    // For the visual indicator bars (value 0.15), show 0 as the label
    const displayValue = value === 0.15 ? 0 : actual;

    if (!displayValue && value !== 0.15) return null; // Show label for 0.15 values but not for actual 0

    return (
      <g transform={`translate(${x + width / 2}, ${y - 8})`} textAnchor="middle">
        <text
          style={{
            fontWeight: 'bold',
            fontSize: '14px',
            fill: 'var(--q3-neutral-default)',
          }}
        >
          {displayValue}
        </text>
      </g>
    );
  };

  if (!selectedSubject || !chartData.length) return null;

  // Calculate max value for proper Y-axis scaling
  const maxValue = Math.max(...chartData.flatMap((d) => visibleGraphOptions.map((opt) => d?.[opt.title] ?? 0))) * 1.1;
  const minWidth = Math.max(chartData.length * visibleGraphOptions.length * 50, 400);

  return (
    <div className="border border-[var(--q3-stroke-light)] rounded-b-xl border-t-0">
      <style jsx>{`
        .checkbox-custom[data-state='checked'] {
          background-color: var(--checkbox-bg) !important;
          border-color: var(--checkbox-border) !important;
        }
        .checkbox-custom:hover {
          border-color: var(--checkbox-border) !important;
        }
        .checkbox-custom:focus-visible {
          ring-color: var(--checkbox-border) !important;
        }
      `}</style>
      <div className="p-4">
        <div className="flex flex-wrap gap-4 mb-4">
          {selectedGraphOptions.map((opt, index) => (
            <div
              key={opt.key}
              className="flex items-center gap-2 border-b-2 cursor-pointer"
              style={{
                borderBottomColor: opt.checked ? opt.color : 'transparent',
              }}
            >
              <Checkbox
                id={`checkbox-${opt.key}`}
                checked={opt.checked}
                onCheckedChange={() => handleOnChange(index)}
                className="checkbox-custom cursor-pointer rounded-[4px] border-[var(--q3-stroke-normal)]"
                style={{
                  '--checkbox-bg': opt.color,
                  '--checkbox-border': opt.color,
                }}
              />
              <label
                htmlFor={`checkbox-${opt.key}`}
                className="text-sm cursor-pointer text-[var(--q3-neutral-default)]"
              >
                {opt.title}
              </label>
            </div>
          ))}
        </div>

        <div className="overflow-x-auto pointer-events-none">
          <div style={{ minWidth }}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} barSize={35} barGap={5} barCategoryGap="15%">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--q3-stroke-light)" />
                <XAxis
                  dataKey="attemptType"
                  tick={{ fontSize: 14, fill: 'var(--q3-neutral-light)' }}
                  axisLine={{ stroke: 'var(--q3-stroke-light)' }}
                />
                <YAxis
                  tickFormatter={(value) => `${value} Qs`}
                  tick={{ fontSize: 12, fill: 'var(--q3-neutral-default)' }}
                  axisLine={{ stroke: 'var(--q3-stroke-light)' }}
                  domain={[0, Math.ceil(maxValue)]}
                />
                {visibleGraphOptions.map((opt) => (
                  <Bar
                    key={opt.title}
                    dataKey={opt.title}
                    fill={opt.color}
                    label={<CustomBarLabel />}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
